import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client.ts';
import { ChevronDown, Search, ArrowRight, FileText, CheckCircle2, Clock, Check, Loader2, PlayCircle, MoreHorizontal, Info, Trash2, Calendar, FileDown, PlusCircle, PenLine, Settings2, Download, Building2, User, X, Filter, RotateCcw } from 'lucide-react';
import { useSessionState } from '../hooks/useSessionState.ts';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';
import * as XLSX from 'xlsx';
import { DEPARTMENTS } from '@cg-idea/common';

export interface IdeaItem {
  id: string;
  title: string;
  description: string;
  department: string;
  category: string;
  evaluationCategory?: string;
  status: string;
  priority: string;
  costSaving?: number;
  createdAt: string;
  updatedAt?: string;
  employee: {
    name: string;
    email: string;
    mobile?: string;
    department?: string;
    mpId?: string;
  };
  timelines?: {
    action?: string;
    statusFrom: string;
    statusTo: string;
    comment: string;
    createdAt: string;
  }[];
  assignees?: {
    userId: string;
    user: {
      name: string;
      email: string;
    }
  }[];
  validation?: {
    annualSavings?: string;
  };
  emailConfirmation?: {
    approvedAmount?: string;
    confirmationDate?: string;
    createdAt?: string;
  };
  misReview?: {
    misStatus?: 'APPROVED' | 'REJECTED' | null;
  } | null;
}

const getInitials = (name: string) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const getAvatarColor = (name: string) => {
  if (!name) return 'bg-slate-100 text-slate-700 border-slate-200';
  const colors = [
    'bg-blue-100 text-blue-700 border-blue-200',
    'bg-emerald-100 text-emerald-700 border-emerald-200',
    'bg-amber-100 text-amber-700 border-amber-200',
    'bg-purple-100 text-purple-700 border-purple-200',
    'bg-pink-100 text-pink-700 border-pink-200',
    'bg-indigo-100 text-indigo-700 border-indigo-200',
    'bg-rose-100 text-rose-700 border-rose-200',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

interface IdeasFilterTableProps {
  queryKeyPrefix: string;
  apiEndpoint: string;
  showExport?: boolean;
  showFuturePotentialByEc?: boolean;
  allowDelete?: boolean;
  allowBulkAssign?: boolean;
  onDelete?: (id: string, reason: string) => void;
  onBulkDelete?: (ids: string[], reason: string) => Promise<void>;
  onBulkAssign?: (selectedIdeas: IdeaItem[], clearSelection: () => void) => void;
  actionButtonLabel?: string;
  evaluatePath?: (id: string) => string;
  includeMisStatuses?: boolean;
  misQueueOnly?: boolean;
  misExport?: boolean;
  onMisEdit?: (ideaId: string) => void;
  groupsEndpoint?: string;
  mappingsEndpoint?: string;
  assigneeId?: string;
  baseDepartment?: string;
}

export function IdeasFilterTable({
  queryKeyPrefix,
  apiEndpoint,
  showExport = false,
  showFuturePotentialByEc = false,
  allowDelete = false,
  allowBulkAssign = false,
  onDelete,
  onBulkDelete,
  onBulkAssign,
  actionButtonLabel = "Evaluate",
  evaluatePath,
  includeMisStatuses = false,
  misQueueOnly = false,
  misExport = false,
  onMisEdit,
  groupsEndpoint = '/admin/department-groups',
  mappingsEndpoint = '/admin/user-role-mappings',
  assigneeId,
  baseDepartment
}: IdeasFilterTableProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const userRole = localStorage.getItem('userRole') || '';
  const userSubCoreRole = localStorage.getItem('userSubCoreRole') || '';
  const isExecutorMode = userSubCoreRole === 'EXECUTOR' || userRole === 'EXECUTION_COMMITTEE' || userRole === 'EXTERNAL_COMMITTEE';

  const [search, setSearch] = useSessionState(`${queryKeyPrefix}_search`, '');
  const [departments, setDepartments] = useSessionState<string[]>(`${queryKeyPrefix}_departments`, []);
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
  const [deptSearch, setDeptSearch] = useState('');
  const deptDropdownRef = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useSessionState<string[]>(`${queryKeyPrefix}_status`, []);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!misQueueOnly) return;
    const allowed = new Set(['EC_EMAIL_CONFIRMED', 'APPROVED', 'MIS_APPROVED', 'MIS_REJECTED']);
    const next = status.filter((value) => allowed.has(value));
    if (next.length !== status.length) setStatus(next);
  }, [misQueueOnly, status, setStatus]);

  const [category, setCategory] = useSessionState(`${queryKeyPrefix}_category`, '');

  const [evaluationCategory, setEvaluationCategory] = useSessionState<string[]>(`${queryKeyPrefix}_evaluationCategory`, []);
  const [isEvalCategoryDropdownOpen, setIsEvalCategoryDropdownOpen] = useState(false);
  const evalCategoryDropdownRef = useRef<HTMLDivElement>(null);

  const [assignmentStatus, setAssignmentStatus] = useSessionState(`${queryKeyPrefix}_assignmentStatus`, '');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [employeeEmail, setEmployeeEmail] = useSessionState(`${queryKeyPrefix}_employeeEmail`, '');
  const [selectedEmployeeName, setSelectedEmployeeName] = useSessionState(`${queryKeyPrefix}_selectedEmployeeName`, '');
  const [employeeSearchInput, setEmployeeSearchInput] = useSessionState(`${queryKeyPrefix}_employeeSearchInput`, '');
  const [employeeSuggestions, setEmployeeSuggestions] = useState<any[]>([]);
  const [isSearchingEmployee, setIsSearchingEmployee] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const employeeDropdownRef = useRef<HTMLDivElement>(null);

  const [startDate, setStartDate] = useSessionState(`${queryKeyPrefix}_startDate`, '');
  const [endDate, setEndDate] = useSessionState(`${queryKeyPrefix}_endDate`, '');

  const [isExporting, setIsExporting] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [selectedUnderlyingDepartments, setSelectedUnderlyingDepartments] = useSessionState<string[]>(`${queryKeyPrefix}_selectedUnderlyingDepartments`, []);
  const [isUnderlyingDeptDropdownOpen, setIsUnderlyingDeptDropdownOpen] = useState(false);
  const [underlyingDeptSearch, setUnderlyingDeptSearch] = useState('');
  const underlyingDeptDropdownRef = useRef<HTMLDivElement>(null);

  const allItemsMapRef = useRef<Map<string, IdeaItem>>(new Map());

  const reviewMutation = useMutation({
    mutationFn: async ({ id, status, comment }: { id: string, status: string, comment: string }) => {
      await apiClient.post(`/admin/ideas/${id}/review`, { status, comment });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
    }
  });

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target as Node)) {
        setIsDeptDropdownOpen(false);
      }
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target as Node)) {
        setShowEmployeeDropdown(false);
      }
      if (underlyingDeptDropdownRef.current && !underlyingDeptDropdownRef.current.contains(event.target as Node)) {
        setIsUnderlyingDeptDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
      if (evalCategoryDropdownRef.current && !evalCategoryDropdownRef.current.contains(event.target as Node)) {
        setIsEvalCategoryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { data: departmentGroupsData } = useQuery({
    queryKey: groupsEndpoint === '/admin/department-groups' ? ['department-groups'] : ['department-groups', groupsEndpoint],
    queryFn: async () => {
      const res = await apiClient.get(groupsEndpoint);
      return res.data.data || [];
    }
  });

  const departmentGroups = departmentGroupsData || [];

  const { data: userRoleMappingsData } = useQuery({
    queryKey: mappingsEndpoint === '/admin/user-role-mappings' ? ['userRoleMappings'] : ['userRoleMappings', mappingsEndpoint],
    queryFn: async () => {
      try {
        const res = await apiClient.get(mappingsEndpoint);
        return res.data.data || [];
      } catch (err) {
        return [];
      }
    }
  });

  const userRoleMappings = userRoleMappingsData || [];

  const availableUnderlyingDepartments = React.useMemo(() => {
    let groupsToUse = departmentGroups;
    if (departments.length > 0) {
      groupsToUse = departmentGroups.filter((g: any) => departments.includes(g.name));
    }
    const allDepts = groupsToUse.flatMap((g: any) => g.departments);
    return Array.from(new Set(allDepts)).sort();
  }, [departmentGroups, departments]);

  React.useEffect(() => {
    if (employeeSearchInput.trim().length < 2) {
      setEmployeeSuggestions([]);
      return;
    }
    if (employeeSearchInput === selectedEmployeeName) {
      // Don't search if the input exactly matches the selected filter (avoid refetch after selection)
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsSearchingEmployee(true);
      try {
        const res = await apiClient.get(`/employees/search?q=${employeeSearchInput}`);
        setEmployeeSuggestions(res.data.data);
      } catch (err) {
        console.error('Error searching employees:', err);
      } finally {
        setIsSearchingEmployee(false);
      }
    }, 250);

    return () => clearTimeout(delayDebounceFn);
  }, [employeeSearchInput, employeeEmail]);

  // Fetch All submitted ideas with filters
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: [queryKeyPrefix, search, departments, selectedUnderlyingDepartments, status.join(','), category, evaluationCategory.join(','), employeeEmail, startDate, endDate, currentPage, assignmentStatus, assigneeId, baseDepartment],
    queryFn: async () => {
      const res = await apiClient.get(apiEndpoint, {
        params: {
          search,
          department: (() => {
            let activeDepts: string[] = [];
            if (selectedUnderlyingDepartments.length > 0) {
              activeDepts = selectedUnderlyingDepartments;
            } else if (departments.length > 0) {
              activeDepts = departments.flatMap(name => {
                const group = departmentGroups.find((g: any) => g.name === name);
                return group ? group.departments : [];
              });
            }

            if (baseDepartment) {
              const baseArr = baseDepartment.split('|');
              if (activeDepts.length > 0) {
                return activeDepts.filter(d => baseArr.includes(d)).join('|') || '__NONE__';
              } else {
                return baseDepartment;
              }
            } else {
              return activeDepts.length > 0 ? activeDepts.join('|') : undefined;
            }
          })(),
          status: status.length > 0 ? status.join(',') : undefined,
          category,
          evaluationCategory: evaluationCategory.length > 0 ? evaluationCategory.join(',') : undefined,
          employeeEmail: employeeEmail || undefined,
          startDate,
          endDate,
          assignmentStatus: assignmentStatus || undefined,
          assigneeId: assigneeId || undefined,
          limit: 15,
          page: currentPage
        },
      });
      return res.data.data;
    },
  });

  React.useEffect(() => {
    if (listData?.items) {
      listData.items.forEach((item: IdeaItem) => {
        allItemsMapRef.current.set(item.id, item);
      });
    }
  }, [listData]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const res = await apiClient.get(apiEndpoint, {
        params: {
          search,
          department: (() => {
            let activeDepts: string[] = [];
            if (selectedUnderlyingDepartments.length > 0) {
              activeDepts = selectedUnderlyingDepartments;
            } else if (departments.length > 0) {
              activeDepts = departments.flatMap(name => {
                const group = departmentGroups.find((g: any) => g.name === name);
                return group ? group.departments : [];
              });
            }

            if (baseDepartment) {
              const baseArr = baseDepartment.split('|');
              if (activeDepts.length > 0) {
                return activeDepts.filter(d => baseArr.includes(d)).join('|') || '__NONE__';
              } else {
                return baseDepartment;
              }
            } else {
              return activeDepts.length > 0 ? activeDepts.join('|') : undefined;
            }
          })(),
          status: status.length > 0 ? status.join(',') : undefined,
          category,
          evaluationCategory: evaluationCategory.length > 0 ? evaluationCategory.join(',') : undefined,
          employeeEmail: employeeEmail || undefined,
          startDate,
          endDate,
          assignmentStatus: assignmentStatus || undefined,
          assigneeId: assigneeId || undefined,
          limit: 100000,
          page: 1
        },
      });

      const data = res.data.data.items;

      if (misExport) {
        const formatWhen = (value?: string | null) => {
          if (!value) return '';
          return new Date(value).toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true,
          });
        };
        const latestWhen = (item: IdeaItem, action: string) => {
          const logs = (item.timelines || []).filter((entry) => entry.action === action);
          if (logs.length === 0) return '';
          const latest = [...logs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
          return formatWhen(latest.createdAt);
        };
        const ideaStatusLabel = (item: IdeaItem) => {
          const emailAmt = item.emailConfirmation?.approvedAmount
            ? parseInt(item.emailConfirmation.approvedAmount.replace(/\D/g, ''), 10)
            : 0;
          if (item.status === 'PENDING_EXTERNAL_APPROVAL' && !isNaN(emailAmt) && emailAmt > 0) return 'EC Head Approved by Email';
          switch (item.status) {
            case 'SUBMITTED': return 'Yet to Be Reviewed';
            case 'WORK_IN_PROGRESS': return 'WIP';
            case 'UNDER_REVIEW': return 'Reviewed';
            case 'PENDING_HEAD_APPROVAL': return 'Pending IC Head Approval';
            case 'PENDING_EXTERNAL_APPROVAL': return 'Requested EC Head Approval';
            case 'APPROVED': return 'EC Head Approved';
            case 'REJECTED': return 'Rejected';
            case 'MORE_INFO_REQUESTED': return 'Hold';
            case 'DUPLICATE': return 'Reject (Duplicate)';
            default: return (item.status || '').replace(/_/g, ' ');
          }
        };
        const csvCell = (value: unknown) => {
          const text = value == null ? '' : String(value);
          return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        };
        const headers = [
          'Ticket ID',
          'Idea Name',
          'Description',
          'Committee Name',
          'Committee Phone',
          'Committee Head Name',
          'Phone Number',
          'Department Name',
          'Employee Name',
          'Idea Status',
          'MIS Name',
          'MIS Contact Number',
          'MIS Description',
          'MIS Approved Date & Time',
          'MIS Rejected Date & Time',
        ];
        const rows = data.map((item: IdeaItem) => {
          const group = departmentGroups.find((entry: { departments?: string[]; name?: string }) => entry.departments?.includes(item.department));
          const committeeName = group?.name || 'Unassigned';
          const heads = userRoleMappings.filter((user: { role?: string; name?: string; managedDepartments?: string[]; mobile?: string }) =>
            user.role === 'COMMITTEE_HEAD' &&
            (user.managedDepartments?.includes(committeeName) || user.managedDepartments?.includes(item.department))
          ) as { role?: string; name?: string; managedDepartments?: string[]; mobile?: string }[];
          const committeePhone = Array.from(new Set(heads.map((user) => user.mobile).filter(Boolean))).join(', ');
          const committeeHeadName = heads.map((user) => user.name).filter(Boolean).join(', ');
          const committeeHeadPhone = heads.map((user) => user.mobile).filter(Boolean).join(', ');
          const misReview = (item as IdeaItem & { misReview?: { misDescription?: string; decidedBy?: { name?: string; mobile?: string } } }).misReview;
          const decidedBy = misReview?.decidedBy;
          return [
            `CG${item.id.substring(0, 7).toUpperCase()}`,
            item.title,
            item.description || '',
            committeeName,
            committeePhone,
            committeeHeadName,
            committeeHeadPhone,
            item.department || '',
            item.employee?.name || '',
            ideaStatusLabel(item),
            decidedBy?.name || '',
            decidedBy?.mobile || '',
            misReview?.misDescription || '',
            latestWhen(item, 'MIS_APPROVED'),
            latestWhen(item, 'MIS_REJECTED'),
          ];
        });
        const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `MIS_Report_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }

      const exportData = data.map((item: IdeaItem, index: number) => {
        const desc1 = item.description ? item.description.substring(0, 32000) : '';
        const group = departmentGroups.find((g: any) => g.departments.includes(item.department));
        const committeeName = group ? group.name : 'Unassigned';

        const getStatusLabel = (s: string, idea?: IdeaItem) => {
          if (!s) return 'Unknown';
          const emailAmt = idea?.emailConfirmation?.approvedAmount
            ? parseInt(idea.emailConfirmation.approvedAmount.replace(/\D/g, ''), 10)
            : 0;
          if (s === 'PENDING_EXTERNAL_APPROVAL' && !isNaN(emailAmt) && emailAmt > 0) {
            return 'EC Head Approved by Email';
          }
          switch (s) {
            case 'SUBMITTED': return 'Yet to Be Reviewed';
            case 'WORK_IN_PROGRESS': return 'WIP';
            case 'UNDER_REVIEW': return 'Reviewed';
            case 'PENDING_HEAD_APPROVAL': return 'Pending IC Head Approval';
            case 'PENDING_EXTERNAL_APPROVAL': return 'Requested EC Head Approval';
            case 'APPROVED': return 'EC Head Approved';
            case 'EXTERNAL_REJECTED': return 'EC Head Rejected';
            case 'HEAD_REJECTED': return 'IC Head Rejected';
            case 'REJECTED': return 'Rejected';
            case 'MORE_INFO_REQUESTED': return 'Hold';
            case 'DUPLICATE': return 'Duplicate';
            case 'EC_EMAIL_CONFIRMED': return 'EC Head Approved by Email';
            default: return s.replace(/_/g, ' ');
          }
        };

        // 1) Assignee Name
        const assigneeNames = item.assignees?.map((a: any) => a.user?.name || a.name).filter(Boolean) || [];
        const assigneeName = assigneeNames.length > 0 ? assigneeNames.join(', ') : 'Unassigned';

        // 2) Committee Head Name
        let headNames: string[] = [];
        if (item.assignees && item.assignees.length > 0) {
          item.assignees.forEach((a: any) => {
            const memberUser = userRoleMappings.find((u: any) => u.id === a.userId || u.email === a.user?.email);
            if (memberUser?.reportsToId) {
              const headUser = userRoleMappings.find((u: any) => u.id === memberUser.reportsToId && u.role === 'COMMITTEE_HEAD');
              if (headUser && !headNames.includes(headUser.name)) {
                headNames.push(headUser.name);
              }
            }
          });
        }
        if (headNames.length === 0) {
          const deptHeads = userRoleMappings.filter((u: any) =>
            u.role === 'COMMITTEE_HEAD' &&
            (u.managedDepartments?.includes(committeeName) || u.managedDepartments?.includes(item.department))
          );
          deptHeads.forEach((h: any) => {
            if (!headNames.includes(h.name)) {
              headNames.push(h.name);
            }
          });
        }
        const headName = headNames.length > 0 ? headNames.join(', ') : 'N/A';

        // 3) Committee Leader Name
        let leaderNames: string[] = [];
        if (headNames.length > 0) {
          userRoleMappings.forEach((u: any) => {
            if (u.role === 'COMMITTEE_HEAD' && headNames.includes(u.name) && u.reportsToId) {
              const leaderUser = userRoleMappings.find((l: any) => l.id === u.reportsToId && l.role === 'LEADER');
              if (leaderUser && !leaderNames.includes(leaderUser.name)) {
                leaderNames.push(leaderUser.name);
              }
            }
          });
        }
        if (leaderNames.length === 0) {
          const deptLeaders = userRoleMappings.filter((u: any) =>
            u.role === 'LEADER' &&
            (u.managedDepartments?.includes(committeeName) || u.managedDepartments?.includes(item.department))
          );
          deptLeaders.forEach((l: any) => {
            if (!leaderNames.includes(l.name)) {
              leaderNames.push(l.name);
            }
          });
        }
        const leaderName = leaderNames.length > 0 ? leaderNames.join(', ') : 'N/A';

        const baseObj = {
          'S.No': index + 1,
          'Ticket ID': `CG${item.id.substring(0, 7).toUpperCase()}`,
          'Name of idea': item.title,
          'Description of the idea': desc1,
          'Committee Name': committeeName,
          'Department where idea to be implemented': item.department,
          'Idea Category': item.category,
          'Eval Category': item.evaluationCategory || 'N/A',
          'Indicative saving': item.costSaving,
          'Committee Leader Name': leaderName,
          'Committee Head Name': headName,
          'Assignee Name': assigneeName,
          'Employee Name': item.employee?.name || 'N/A',
          'Employee ID': item.employee?.mpId || 'N/A',
          'Department': item.employee?.department || 'N/A',
          'Email ID': item.employee?.email || 'N/A',
          'Employee Mobile': item.employee?.mobile || 'N/A',
          'Submitted date and time': new Date(item.createdAt).toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
          }),
        };

        const mainStatusMap: Record<string, string> = {
          'SUBMITTED': 'Yet to Be Reviewed',
          'UNDER_REVIEW': 'Reviewed',
          'WORK_IN_PROGRESS': 'Work in progress',
          'PENDING_HEAD_APPROVAL': 'Pending IC Head Approval',
          'HEAD_APPROVED': 'IC Head Approval',
          'PENDING_EXTERNAL_APPROVAL': 'Requested EC Head Approval',
          'APPROVED': 'Approval',
          'EXTERNAL_COMMITTEE_APPROVED': 'Approval',
          'EXECUTION_APPROVED': 'Approval'
        };

        let previousStatus = '-';
        let previousStatusDate = '-';
        let currentStatusDate = new Date(item.updatedAt || item.createdAt).toLocaleString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true
        });

        if (item.timelines && item.timelines.length > 0) {
          const mainLogs = item.timelines
            .filter((t: any) => mainStatusMap[t.action as string] || mainStatusMap[t.statusTo as string])
            .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          const currentMappedStatus = mainStatusMap[item.status] || getStatusLabel(item.status, item);
          let foundCurrent = false;

          for (const log of mainLogs) {
            const mappedLogStatus = mainStatusMap[log.action as string] || mainStatusMap[log.statusTo as string];

            if (mappedLogStatus === currentMappedStatus && !foundCurrent) {
              currentStatusDate = new Date(log.createdAt).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true
              });
              foundCurrent = true;
            } else if (mappedLogStatus && mappedLogStatus !== currentMappedStatus) {
              previousStatus = mappedLogStatus;
              previousStatusDate = new Date(log.createdAt).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true
              });
              break;
            }
          }
        }

        // Find IC Head Approval timeline entry & details
        let approvedStatus = '-';
        let icHeadApprovalDate = '-';

        if (item.timelines && item.timelines.length > 0) {
          const icApprovalLogs = item.timelines
            .filter((t: any) => {
              const isToExtOrApp = t.statusTo === 'PENDING_EXTERNAL_APPROVAL' || t.statusTo === 'APPROVED';
              const isIcAction =
                t.action === 'COMMITTEE_HEAD_PENDING_EXTERNAL_APPROVAL' ||
                t.action === 'COMMITTEE_HEAD_APPROVED' ||
                t.action === 'ADMIN_APPROVED' ||
                t.action === 'EXTERNAL_COMMITTEE_APPROVED';

              return isToExtOrApp || isIcAction;
            })
            .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

          const icLog = icApprovalLogs[0];
          if (icLog) {
            approvedStatus = 'APPROVED';
            icHeadApprovalDate = new Date(icLog.createdAt).toLocaleString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true
            });
          }
        }

        if (approvedStatus === '-' && (item.status === 'PENDING_EXTERNAL_APPROVAL' || item.status === 'APPROVED')) {
          approvedStatus = 'APPROVED';
          const fallbackDate = (item.validation as any)?.createdAt || item.createdAt;
          if (fallbackDate) {
            icHeadApprovalDate = new Date(fallbackDate).toLocaleString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true
            });
          }
        }

        (baseObj as any)['Status'] = getStatusLabel(item.status, item);
        (baseObj as any)['Status Date & Time'] = currentStatusDate;
        (baseObj as any)['Previous Status'] = previousStatus;
        (baseObj as any)['Previous Status Date & Time'] = previousStatusDate;
        (baseObj as any)['APPROVED Status'] = approvedStatus;
        (baseObj as any)['IC Head Approval Date'] = icHeadApprovalDate;

        let wipValue: number | string = '-';
        if (item.status === 'WORK_IN_PROGRESS') {
          if (item.timelines && item.timelines.length > 0) {
            const wipLogs = item.timelines.filter((t: any) => t.comment?.includes('with estimated amount:'));
            if (wipLogs.length > 0) {
              const latestLog = wipLogs.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
              const match = latestLog.comment?.match(/with estimated amount:\s*(.+)/);
              if (match && match[1]) {
                const val = parseInt(match[1].replace(/\D/g, ''), 10);
                if (!isNaN(val)) wipValue = val;
              }
            }
          }
        }

        let pendingICValue: number | string = '-';
        if (item.status === 'PENDING_HEAD_APPROVAL' && item.validation?.annualSavings) {
          const val = parseInt(item.validation.annualSavings.replace(/\D/g, ''), 10);
          if (!isNaN(val)) pendingICValue = val;
        }

        let pendingECValue: number | string = '-';
        const emailConfirmAmt = item.emailConfirmation?.approvedAmount
          ? parseInt(item.emailConfirmation.approvedAmount.replace(/\D/g, ''), 10)
          : 0;
        const hasEmailConfirm = !isNaN(emailConfirmAmt) && emailConfirmAmt > 0;
        // Same as status filter / card: Requested EC excludes email-confirmed ideas
        if (
          item.status === 'PENDING_EXTERNAL_APPROVAL' &&
          !hasEmailConfirm &&
          item.validation?.annualSavings
        ) {
          const val = parseInt(item.validation.annualSavings.replace(/\D/g, ''), 10);
          if (!isNaN(val)) pendingECValue = val;
        }

        let ecApprovedValue: number | string = '-';
        if (item.status === 'APPROVED' && item.validation?.annualSavings) {
          const val = parseInt(item.validation.annualSavings.replace(/\D/g, ''), 10);
          if (!isNaN(val)) ecApprovedValue = val;
        }

        let ecRejectedValue: number | string = '-';
        if (item.status === 'REJECTED' && item.timelines?.some((t: any) => t.action === 'EXTERNAL_COMMITTEE_REJECTED')) {
          if (item.validation?.annualSavings) {
            const val = parseInt(item.validation.annualSavings.replace(/\D/g, ''), 10);
            if (!isNaN(val)) ecRejectedValue = val;
          }
        }

        let ecEmailConfirmedValue: number | string = '-';
        if (hasEmailConfirm) {
          ecEmailConfirmedValue = emailConfirmAmt;
        }

        let ecEmailConfirmationDate: string = '-';
        if (item.emailConfirmation) {
          const rawDate = item.emailConfirmation.confirmationDate || item.emailConfirmation.createdAt;
          if (rawDate) {
            ecEmailConfirmationDate = new Date(rawDate).toLocaleDateString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric'
            });
          }
        }

        if (!isExecutorMode) {
          const parseExportAmount = (raw: unknown): number => {
            if (typeof raw === 'number') return raw;
            if (typeof raw !== 'string' || !raw || raw === '-') return 0;
            const val = parseInt(raw.replace(/\D/g, ''), 10);
            return isNaN(val) ? 0 : val;
          };

          let icHeadApprovedValue: number | string = '-';
          const isIcHeadApproved =
            item.status === 'PENDING_EXTERNAL_APPROVAL' ||
            item.status === 'APPROVED' ||
            item.timelines?.some((t: any) =>
              t.statusTo === 'PENDING_EXTERNAL_APPROVAL' ||
              t.statusTo === 'APPROVED' ||
              (typeof t.action === 'string' && (t.action.includes('APPROVED') || t.action.includes('EVALUATED')))
            );

          if (isIcHeadApproved) {
            let original = parseExportAmount(item.validation?.annualSavings);
            const revisionLog = item.timelines?.find((t: any) =>
              t.comment?.includes('[Annual Savings revised from')
            );
            if (revisionLog?.comment) {
              const match = revisionLog.comment.match(/\[Annual Savings revised from (.*?) to/);
              if (match?.[1]) {
                const fromVal = parseExportAmount(match[1]);
                if (fromVal > 0) original = fromVal;
              }
            }
            if (original > 0) icHeadApprovedValue = original;
          }

          (baseObj as any)['WIP Value'] = wipValue;
          (baseObj as any)['Pending IC Head Approval Value'] = pendingICValue;
          (baseObj as any)['Requested EC Head Approval Value'] = pendingECValue;
          (baseObj as any)['IC Head Approved Value'] = icHeadApprovedValue;
          (baseObj as any)['EC Head Approved Value'] = ecApprovedValue;
          // Future potential by EC export: shortfall only (Approved / Email Confirmed).
          // Requested EC pending (no email confirm) excluded — matches KPI card.
          if (showFuturePotentialByEc) {
            const hasEmailConfirm = typeof ecEmailConfirmedValue === 'number' && ecEmailConfirmedValue > 0;
            const isEcFuturePipeline =
              item.status === 'APPROVED' || hasEmailConfirm;
            if (!isEcFuturePipeline) {
              (baseObj as any)['Future potential by EC'] = '-';
            } else {
              let original = parseExportAmount(item.validation?.annualSavings);
              const revisionLog = item.timelines?.find((t: any) =>
                t.comment?.includes('[Annual Savings revised from')
              );
              if (revisionLog?.comment) {
                const match = revisionLog.comment.match(/\[Annual Savings revised from (.*?) to/);
                if (match?.[1]) {
                  const fromVal = parseExportAmount(match[1]);
                  if (fromVal > 0) original = fromVal;
                }
              }
              let confirmed = 0;
              if (item.status === 'APPROVED') {
                confirmed = parseExportAmount(item.validation?.annualSavings);
              } else if (hasEmailConfirm) {
                confirmed = ecEmailConfirmedValue as number;
              }
              const remaining = Math.max(0, original - confirmed);
              (baseObj as any)['Future potential by EC'] = remaining > 0 ? remaining : '-';
            }
          }
          (baseObj as any)['EC Head Rejected Value'] = ecRejectedValue;
          (baseObj as any)['EC Head Email Confirmed Value'] = ecEmailConfirmedValue;
          (baseObj as any)['EC Head Email Confirmation Date'] = ecEmailConfirmationDate;
        }

        return baseObj;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ideas');
      XLSX.writeFile(workbook, `Ideas_Export_${new Date().toISOString().split('T')[0]}.xlsx`);

    } catch (err) {
      console.error('Failed to export', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkDelete = async () => {
    const selectedIds = Object.keys(rowSelection).filter(id => (rowSelection as any)[id]);
    const items = (listData?.items || []) as IdeaItem[];
    const editableIds = selectedIds.filter((id) => items.find((item) => item.id === id)?.misReview?.misStatus !== 'APPROVED');
    if (editableIds.length === 0 || !onBulkDelete) {
      if (selectedIds.length > 0 && editableIds.length === 0) {
        alert('MIS approved ideas can only be viewed.');
      }
      return;
    }

    const reason = window.prompt(`Enter reason for deleting ${editableIds.length} ideas:`);
    if (reason !== null) {
      if (reason.trim() === '') {
        alert('Reason is required to delete ideas.');
        return;
      }

      setIsBulkDeleting(true);
      try {
        await onBulkDelete(editableIds, reason);
        setRowSelection({});
        if (editableIds.length < selectedIds.length) {
          alert('MIS approved ideas were left unchanged. They can only be viewed.');
        }
      } catch (err) {
        console.error(err);
        alert('Failed to delete some ideas.');
      } finally {
        setIsBulkDeleting(false);
      }
    }
  };

  const columnHelper = createColumnHelper<IdeaItem>();
  const columns = [
    ...(((allowDelete && onBulkDelete) || (allowBulkAssign && onBulkAssign)) ? [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4 translate-y-0.5"
            title="Select all on page"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4 translate-y-0.5"
          />
        ),
      })
    ] : []),
    columnHelper.display({
      id: 'sno',
      header: '#',
      cell: (info) => {
        const rowIndex = info.row.index;
        const serialNumber = (currentPage - 1) * 15 + rowIndex + 1;
        return <span className="text-sm font-semibold text-slate-600">{serialNumber}</span>;
      }
    }),
    columnHelper.accessor('id', {
      header: 'ID / Committee',
      cell: (info) => {
        const deptName = info.row.original.department;
        const group = departmentGroups.find((g: any) => g.departments.includes(deptName));
        return (
          <div className="flex flex-col gap-1.5 min-w-[130px]">
            <span className="font-semibold text-slate-500 text-xs whitespace-nowrap uppercase">
              CG{info.getValue().substring(0, 7)}
            </span>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5 leading-none">
                {group ? group.name : 'Unassigned'}
              </span>
              <span className="text-xs font-semibold text-slate-700 leading-tight">{deptName}</span>
            </div>
          </div>
        );
      }
    }),
    columnHelper.accessor('title', {
      header: 'Idea / Emp Details',
      cell: (info) => (
        <div className="min-w-[200px] max-w-[300px] whitespace-normal">
          <p className="font-semibold text-slate-800 text-sm line-clamp-2" title={info.getValue() as string}>{info.getValue()}</p>
          <p className="text-[11px] text-slate-500 mt-1 flex items-center flex-wrap gap-1">
            <span className="font-medium text-slate-700">{info.row.original.employee?.name}</span>
            <span className="text-slate-300">•</span>
            <span>{info.row.original.employee?.department || info.row.original.department}</span>
            {info.row.original.employee?.mobile && (
              <>
                <span className="text-slate-300">•</span>
                <span className="text-slate-600">{info.row.original.employee.mobile}</span>
              </>
            )}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor('evaluationCategory', {
      header: 'Category',
      cell: (info) => {
        const val = info.getValue();
        return val ? <span className="font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">{val}</span> : <span className="text-slate-400">-</span>;
      }
    }),
    columnHelper.accessor('costSaving', {
      header: 'Ind. Savings',
      cell: (info) => {
        const val = info.getValue();
        return val ? <span className="text-sm font-semibold text-emerald-600">₹{val.toLocaleString('en-IN')}</span> : <span className="text-slate-400 text-sm">-</span>;
      }
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const val = info.getValue();
        const idea = info.row.original;
        const emailAmt = idea.emailConfirmation?.approvedAmount
          ? parseInt(idea.emailConfirmation.approvedAmount.replace(/\D/g, ''), 10)
          : 0;
        const isEmailConfirmedPending =
          val === 'PENDING_EXTERNAL_APPROVAL' && !isNaN(emailAmt) && emailAmt > 0;

        let color = 'bg-slate-100 text-slate-600';
        if (val === 'APPROVED') color = 'bg-emerald-100 text-emerald-700';
        if (val === 'REJECTED') color = 'bg-red-100 text-red-700';
        if (val === 'DUPLICATE') color = 'bg-slate-200 text-slate-800';
        if (val === 'UNDER_REVIEW') color = 'bg-blue-100 text-blue-700';
        if (val === 'MORE_INFO_REQUESTED') color = 'bg-amber-100 text-amber-700';
        if (val === 'SUBMITTED') color = 'bg-indigo-100 text-indigo-700';
        if (val === 'PENDING_HEAD_APPROVAL') color = 'bg-orange-100 text-orange-700';
        if (val === 'PENDING_EXTERNAL_APPROVAL') color = 'bg-purple-100 text-purple-700';
        if (isEmailConfirmedPending) color = 'bg-teal-100 text-teal-700';

        let displayVal = val.replace(/_/g, ' ');
        switch (val) {
          case 'SUBMITTED': displayVal = 'Yet to Be Reviewed'; break;
          case 'WORK_IN_PROGRESS': displayVal = 'WIP'; break;
          case 'UNDER_REVIEW': displayVal = 'Reviewed'; break;
          case 'PENDING_HEAD_APPROVAL': displayVal = 'Pending IC Head Approval'; break;
          case 'PENDING_EXTERNAL_APPROVAL': displayVal = 'Requested EC Head Approval'; break;
          case 'APPROVED': displayVal = 'EC Head Approved'; break;
          case 'EXTERNAL_REJECTED': displayVal = 'EC Head Rejected'; break;
          case 'HEAD_REJECTED': displayVal = 'IC Head Rejected'; break;
          case 'REJECTED': displayVal = 'Rejected'; break;
          case 'MORE_INFO_REQUESTED': displayVal = 'Hold'; break;
          case 'DUPLICATE': displayVal = misQueueOnly ? 'Reject (Duplicate)' : 'Duplicate'; break;
        }
        if (isEmailConfirmedPending) displayVal = 'EC Head Approved by Email';

        let tooltipContent = '';
        if (['REJECTED', 'APPROVED', 'MORE_INFO_REQUESTED', 'PENDING_HEAD_APPROVAL', 'PENDING_EXTERNAL_APPROVAL', 'DUPLICATE'].includes(val)) {
          const timelines = info.row.original.timelines || [];
          const relevantLog = [...timelines].reverse().find(t => t.statusTo === val && t.statusFrom !== val);
          if (relevantLog && relevantLog.comment) {
            tooltipContent = relevantLog.comment;
          }
        }

        return (
          <div className="flex items-center space-x-1.5 group/status">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${color}`}>
              {displayVal}
            </span>
            {tooltipContent && (
              <div className="relative flex items-center justify-center">
                <Info className="w-4 h-4 text-slate-400 hover:text-slate-600 transition-colors cursor-help" />
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-48 p-2.5 bg-slate-800 text-white text-[11px] rounded-lg opacity-0 invisible group-hover/status:opacity-100 group-hover/status:visible transition-all z-10 shadow-lg break-words whitespace-normal text-center pointer-events-none font-medium leading-relaxed">
                  {tooltipContent}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                </div>
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Received On',
      cell: (info) => <span className="text-slate-500 text-sm">{new Date(info.getValue()).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>,
    }),
    ...(userRole !== 'COMMITTEE_MEMBER' && userRole !== 'EXTERNAL_COMMITTEE' ? [
      columnHelper.accessor('assignees', {
        header: 'Assignees',
        cell: (info) => {
          const assignees = info.getValue() || [];
          if (assignees.length === 0) return <span className="text-slate-400 text-[11px] italic">Unassigned</span>;

          if (assignees.length === 1) {
            const a = assignees[0];
            const initials = getInitials(a.user.name);
            const colorClass = getAvatarColor(a.user.name);
            return (
              <div className="relative group/avatar inline-flex items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm cursor-help ${colorClass}`}>
                  {initials}
                </div>
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-1 bg-slate-800 text-white text-[11px] rounded-md opacity-0 invisible group-hover/avatar:opacity-100 group-hover/avatar:visible transition-all z-10 shadow-lg pointer-events-none">
                  {a.user.name}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                </div>
              </div>
            );
          }

          return (
            <div className="flex items-center -space-x-2">
              {assignees.slice(0, 3).map((a: any, i: number) => {
                const initials = getInitials(a.user.name);
                const colorClass = getAvatarColor(a.user.name);
                return (
                  <div key={i} className="relative group/avatar">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm cursor-help ${colorClass}`}>
                      {initials}
                    </div>
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-1 bg-slate-800 text-white text-[11px] rounded-md opacity-0 invisible group-hover/avatar:opacity-100 group-hover/avatar:visible transition-all z-10 shadow-lg pointer-events-none">
                      {a.user.name}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                    </div>
                  </div>
                );
              })}
              {assignees.length > 3 && (
                <div className="relative group/more">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm cursor-help bg-slate-100 text-slate-600">
                    +{assignees.length - 3}
                  </div>
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-1.5 bg-slate-800 text-white text-[11px] rounded-md opacity-0 invisible group-hover/more:opacity-100 group-hover/more:visible transition-all z-10 shadow-lg pointer-events-none">
                    <div className="flex flex-col gap-0.5 items-center">
                      {assignees.slice(3).map((a: any, i: number) => (
                        <span key={i}>{a.user.name}</span>
                      ))}
                    </div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                  </div>
                </div>
              )}
            </div>
          );
        }
      })
    ] : []),
    columnHelper.display({
      id: 'actions',
      header: 'actions',
      cell: (info) => (
        <div className="flex items-center space-x-2">
          {onMisEdit && (
            <button
              type="button"
              onClick={() => onMisEdit(info.row.original.id)}
              className="bg-slate-50 hover:bg-slate-100 text-slate-600 p-1.5 rounded-lg transition-colors cursor-pointer border border-slate-200"
              title="MIS Decision"
            >
              <PenLine className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => navigate(evaluatePath ? evaluatePath(info.row.original.id) : `/ideas/${info.row.original.id}`)}
            className="bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1 transition-all cursor-pointer"
          >
            <span>{actionButtonLabel}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
          {allowDelete && onDelete && info.row.original.misReview?.misStatus !== 'APPROVED' && (
            <button
              onClick={() => {
                const reason = window.prompt('Enter reason for deleting this idea:');
                if (reason !== null) {
                  if (reason.trim() === '') {
                    alert('Reason is required to delete an idea.');
                    return;
                  }
                  onDelete(info.row.original.id, reason);
                }
              }}
              className="bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-lg transition-colors cursor-pointer"
              title="Delete Idea"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: listData?.items || [],
    columns,
    state: {
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getRowId: row => row.id,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      {/* Top Bar: Search and Export */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[250px] relative bg-white rounded-xl shadow-sm border border-slate-200 p-2">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
              <Search className="w-5 h-5" />
            </span>
            <input
              type="text"
              placeholder="Search by title, description, or submitter..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-none rounded-lg text-sm text-slate-800 focus:ring-0 outline-none bg-transparent"
            />
          </div>
        </div>

        {(() => {
          const hasActiveFilters = search || departments.length > 0 || status.length > 0 || category || evaluationCategory.length > 0 || assignmentStatus || employeeEmail || startDate || endDate || selectedUnderlyingDepartments.length > 0;
          const resetFilters = () => {
            setSearch('');
            setDepartments([]);
            setStatus([]);
            setCategory('');
            setEvaluationCategory([]);
            setAssignmentStatus('');
            setEmployeeEmail('');
            setStartDate('');
            setEndDate('');
            setSelectedUnderlyingDepartments([]);
            setSearchParams(new URLSearchParams());
          };

          return hasActiveFilters ? (
            <button
              onClick={resetFilters}
              className="bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 font-semibold px-4 py-2 rounded-xl text-sm transition-all flex items-center justify-center space-x-1.5 cursor-pointer shadow-sm shrink-0 h-[52px]"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
          ) : null;
        })()}

        {showExport && (
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold px-4 py-2 rounded-xl text-sm transition-all flex items-center justify-center space-x-1.5 cursor-pointer shadow-sm disabled:opacity-50 shrink-0 h-[52px]"
          >
            {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span>Export CSV</span>
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-wrap gap-4 items-center [&>div]:flex-auto [&>div]:min-w-[180px] [&>div]:max-w-[280px]">

          <div className={`relative ${isStatusDropdownOpen ? 'z-50' : 'z-20'}`} ref={statusDropdownRef}>
            <Filter className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <div
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-slate-700 text-sm outline-none transition-all cursor-pointer flex items-center justify-between"
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            >
              <span className="truncate">
                {status.length === 0 ? 'All Statuses' : `${status.length} Selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </div>

            {isStatusDropdownOpen && (() => {
              const statusOptions = misQueueOnly ? [
                { value: "EC_EMAIL_CONFIRMED", label: "EC Head Approved by Email" },
                { value: "APPROVED", label: "EC Head Approved" },
                { value: "MIS_APPROVED", label: "MIS Approved" },
                { value: "MIS_REJECTED", label: "Reject (Duplicate)" },
              ] : isExecutorMode ? [
                { value: "PENDING_EXTERNAL_APPROVAL", label: "Requested EC Head Approval" },
                { value: "EC_EMAIL_CONFIRMED", label: "EC Head Approved by Email" },
                { value: "APPROVED", label: "EC Head Approved" },
                { value: "EXTERNAL_REJECTED", label: "EC Head Rejected" },
              ] : [
                { value: "UNDER_REVIEW", label: "Reviewed" },
                { value: "SUBMITTED", label: "Yet to Be Reviewed" },
                { value: "WORK_IN_PROGRESS", label: "WIP" },
                { value: "PENDING_HEAD_APPROVAL", label: "Pending IC Head Approval" },
                { value: "PENDING_EXTERNAL_APPROVAL", label: "Requested EC Head Approval" },
                { value: "EC_EMAIL_CONFIRMED", label: "EC Head Approved by Email" },
                { value: "APPROVED", label: "EC Head Approved" },
                { value: "EXTERNAL_REJECTED", label: "EC Head Rejected" },
                { value: "HEAD_REJECTED", label: "IC Head Rejected" },
                { value: "REJECTED", label: "Rejected" },
                { value: "MORE_INFO_REQUESTED", label: "Hold" },
                { value: "DUPLICATE", label: "Duplicate" },
                ...(includeMisStatuses ? [
                  { value: "MIS_APPROVED", label: "Approved by MIS" },
                  { value: "MIS_REJECTED", label: "Rejected by MIS" },
                ] : []),
              ];
              return (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                <div className="max-h-60 overflow-y-auto py-1">
                  <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                    <input
                      type="checkbox"
                      className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      checked={status.length === statusOptions.length}
                      onChange={(e) => {
                        setStatus(e.target.checked ? statusOptions.map((option) => option.value) : []);
                        setSearchParams(params => { params.set('page', '1'); return params; });
                      }}
                    />
                    <span className="text-sm text-slate-700 font-semibold">Select All</span>
                  </label>
                  {statusOptions.map((opt) => (
                    <label key={opt.value} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={status.includes(opt.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setStatus([...status, opt.value]);
                          } else {
                            setStatus(status.filter(s => s !== opt.value));
                          }
                          setSearchParams(params => { params.set('page', '1'); return params; });
                        }}
                      />
                      <span className="text-sm text-slate-700 leading-tight group-hover:text-blue-700 transition-colors">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              );
            })()}
          </div>

          <div className={`relative ${isEvalCategoryDropdownOpen ? 'z-50' : 'z-20'}`} ref={evalCategoryDropdownRef}>
            <Filter className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <div
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-slate-700 text-sm outline-none transition-all cursor-pointer flex items-center justify-between"
              onClick={() => setIsEvalCategoryDropdownOpen(!isEvalCategoryDropdownOpen)}
            >
              <span className="truncate">
                {evaluationCategory.length === 0 ? 'All Categories' : `${evaluationCategory.length} Selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </div>

            {isEvalCategoryDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                <div className="max-h-60 overflow-y-auto py-1">
                  <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                    <input
                      type="checkbox"
                      className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      checked={evaluationCategory.length === 3}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEvaluationCategory(["A", "B", "C"]);
                        } else {
                          setEvaluationCategory([]);
                        }
                        setSearchParams(params => { params.set('page', '1'); return params; });
                      }}
                    />
                    <span className="text-sm text-slate-700 font-semibold">Select All</span>
                  </label>
                  {[
                    { value: "A", label: "Category A" },
                    { value: "B", label: "Category B" },
                    { value: "C", label: "Category C" }
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={evaluationCategory.includes(opt.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEvaluationCategory([...evaluationCategory, opt.value]);
                          } else {
                            setEvaluationCategory(evaluationCategory.filter(c => c !== opt.value));
                          }
                          setSearchParams(params => { params.set('page', '1'); return params; });
                        }}
                      />
                      <span className="text-sm text-slate-700 leading-tight group-hover:text-blue-700 transition-colors">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {(userRole === 'COMMITTEE_HEAD' || userRole === 'LEADER' || userRole === 'ADMIN') && (
            <div className="relative">
              <Filter className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
              <select
                value={assignmentStatus}
                onChange={(e) => setAssignmentStatus(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-slate-700 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all appearance-none cursor-pointer"
              >
                <option value="">All Assignments</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="UNASSIGNED">Unassigned</option>
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-3 text-slate-400 pointer-events-none" />
            </div>
          )}

          <div className="relative" ref={employeeDropdownRef}>
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search Employee..."
              value={employeeSearchInput}
              onChange={(e) => {
                setEmployeeSearchInput(e.target.value);
                setShowEmployeeDropdown(true);
                if (e.target.value === '') {
                  setEmployeeEmail('');
                  setSelectedEmployeeName('');
                }
              }}
              onFocus={() => setShowEmployeeDropdown(true)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-slate-700 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
            />
            {isSearchingEmployee && (
              <span className="absolute right-3 top-2.5">
                <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
              </span>
            )}

            {showEmployeeDropdown && employeeSuggestions.length > 0 && (
              <div className="absolute z-50 left-0 mt-1.5 w-[280px] bg-white border border-slate-200 rounded-xl shadow-xl max-h-[300px] overflow-y-auto p-1.5 flex flex-col gap-1 animate-in slide-in-from-top-2 duration-150">
                {employeeSuggestions.map((emp) => (
                  <div
                    key={emp.id}
                    onClick={() => {
                      setEmployeeSearchInput(emp.name);
                      setEmployeeEmail(emp.email);
                      setSelectedEmployeeName(emp.name);
                      setShowEmployeeDropdown(false);
                    }}
                    className="px-3 py-2.5 hover:bg-blue-50/80 rounded-lg cursor-pointer transition-all flex flex-col group border border-transparent hover:border-blue-100"
                  >
                    <span className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors">{emp.name}</span>
                    <span className="text-xs text-slate-500 mt-0.5 truncate">{emp.email}</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-1">{emp.department || 'No Dept'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={`relative ${isDeptDropdownOpen ? 'z-50' : 'z-20'}`} ref={deptDropdownRef}>
            <Filter className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <div
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-slate-700 text-sm outline-none transition-all cursor-pointer flex items-center justify-between"
              onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
            >
              <span className="truncate">
                {departments.length === 0 ? 'All Committees' : `${departments.length} Selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </div>

            {isDeptDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search committees..."
                      value={deptSearch}
                      onChange={(e) => setDeptSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto py-1">
                  <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                    <input
                      type="checkbox"
                      className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      checked={
                        departmentGroups.filter((g: any) => g.name.toLowerCase().includes(deptSearch.toLowerCase())).length > 0 &&
                        departmentGroups.filter((g: any) => g.name.toLowerCase().includes(deptSearch.toLowerCase())).every((g: any) => departments.includes(g.name))
                      }
                      onChange={(e) => {
                        const visibleGroups = departmentGroups.filter((g: any) => g.name.toLowerCase().includes(deptSearch.toLowerCase()));
                        const groupNames = visibleGroups.map((g: any) => g.name);
                        if (e.target.checked) {
                          setDepartments(Array.from(new Set([...departments, ...groupNames])));
                        } else {
                          setDepartments(departments.filter(dept => !groupNames.includes(dept)));
                        }
                        setSearchParams(params => { params.set('page', '1'); return params; });
                      }}
                    />
                    <span className="text-sm text-slate-700 font-semibold">Select All</span>
                  </label>
                  {departmentGroups.filter((g: any) => g.name.toLowerCase().includes(deptSearch.toLowerCase())).length === 0 ? (
                    <p className="px-4 py-3 text-xs text-slate-500 text-center">No committees found.</p>
                  ) : (
                    departmentGroups.filter((g: any) => g.name.toLowerCase().includes(deptSearch.toLowerCase())).map((g: any) => (
                      <label key={g.id} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={departments.includes(g.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDepartments([...departments, g.name]);
                            } else {
                              setDepartments(departments.filter(dept => dept !== g.name));
                            }
                            setSearchParams(params => { params.set('page', '1'); return params; });
                          }}
                        />
                        <span className="text-sm text-slate-700 leading-tight group-hover:text-blue-700 transition-colors">{g.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={`relative ${isUnderlyingDeptDropdownOpen ? 'z-50' : 'z-20'}`} ref={underlyingDeptDropdownRef}>
            <Filter className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <div
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-slate-700 text-sm outline-none transition-all cursor-pointer flex items-center justify-between"
              onClick={() => setIsUnderlyingDeptDropdownOpen(!isUnderlyingDeptDropdownOpen)}
            >
              <span className="truncate">
                {selectedUnderlyingDepartments.length === 0 ? 'All Departments' : `${selectedUnderlyingDepartments.length} Selected`}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </div>

            {isUnderlyingDeptDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search departments..."
                      value={underlyingDeptSearch}
                      onChange={(e) => setUnderlyingDeptSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto py-1">
                  <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                    <input
                      type="checkbox"
                      className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      checked={
                        availableUnderlyingDepartments.filter((d: any) => (d as string).toLowerCase().includes(underlyingDeptSearch.toLowerCase())).length > 0 &&
                        availableUnderlyingDepartments.filter((d: any) => (d as string).toLowerCase().includes(underlyingDeptSearch.toLowerCase())).every((d: any) => selectedUnderlyingDepartments.includes(d as string))
                      }
                      onChange={(e) => {
                        const visibleDepts = availableUnderlyingDepartments.filter((d: any) => (d as string).toLowerCase().includes(underlyingDeptSearch.toLowerCase())) as string[];
                        if (e.target.checked) {
                          setSelectedUnderlyingDepartments(Array.from(new Set([...selectedUnderlyingDepartments, ...visibleDepts])));
                        } else {
                          setSelectedUnderlyingDepartments(selectedUnderlyingDepartments.filter(dept => !visibleDepts.includes(dept)));
                        }
                        setSearchParams(params => { params.set('page', '1'); return params; });
                      }}
                    />
                    <span className="text-sm text-slate-700 font-semibold">Select All</span>
                  </label>
                  {availableUnderlyingDepartments.filter((d: any) => (d as string).toLowerCase().includes(underlyingDeptSearch.toLowerCase())).length === 0 ? (
                    <p className="px-4 py-3 text-xs text-slate-500 text-center">No departments found.</p>
                  ) : (
                    availableUnderlyingDepartments.filter((d: any) => (d as string).toLowerCase().includes(underlyingDeptSearch.toLowerCase())).map((dept: any) => (
                      <label key={dept} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={selectedUnderlyingDepartments.includes(dept)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUnderlyingDepartments([...selectedUnderlyingDepartments, dept]);
                            } else {
                              setSelectedUnderlyingDepartments(selectedUnderlyingDepartments.filter(d => d !== dept));
                            }
                            setSearchParams(params => { params.set('page', '1'); return params; });
                          }}
                        />
                        <span className="text-sm text-slate-700 leading-tight group-hover:text-blue-700 transition-colors">{dept}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="!max-w-[350px] !min-w-[300px] flex items-center space-x-2">
            <div className="relative flex-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-9 pr-2 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-sm text-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all cursor-pointer"
              />
              <Calendar className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            </div>
            <span className="text-slate-400 text-sm font-medium">to</span>
            <div className="relative flex-1">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-9 pr-2 py-2 bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-lg text-sm text-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all cursor-pointer"
              />
              <Calendar className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            </div>
          </div>

        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {listLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : (
          <>
            {Object.keys(rowSelection).filter(id => (rowSelection as any)[id]).length > 0 && ((allowDelete && onBulkDelete) || (allowBulkAssign && onBulkAssign)) && (
              <div className="bg-blue-50 px-6 py-3 border-b border-blue-100 flex items-center justify-between transition-all">
                <span className="text-blue-700 font-medium text-sm">
                  {Object.keys(rowSelection).filter(id => (rowSelection as any)[id]).length} ideas selected
                </span>
                <div className="flex items-center space-x-3">
                  {allowDelete && onBulkDelete && (
                    <button
                      onClick={handleBulkDelete}
                      disabled={isBulkDeleting}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-1.5 transition-colors shadow-sm disabled:opacity-50"
                    >
                      {isBulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      <span>Delete Selected</span>
                    </button>
                  )}
                  {allowBulkAssign && onBulkAssign && (
                    <button
                      onClick={() => {
                        const selectedIds = Object.keys(rowSelection).filter(id => (rowSelection as any)[id]);
                        const selectedItems = selectedIds.map(id => allItemsMapRef.current.get(id)).filter(Boolean) as IdeaItem[];
                        onBulkAssign(selectedItems, () => setRowSelection({}));
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-1.5 transition-colors shadow-sm"
                    >
                      <span>Bulk Assign / Un-Assign</span>
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 border-b border-slate-200">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${header.id === 'actions' ? 'sticky right-0 bg-slate-50 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.02)] z-10' : ''}`}
                        >
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center text-slate-400 space-y-2">
                          <Filter className="w-8 h-8 mb-2 opacity-50" />
                          <p className="text-lg font-medium text-slate-600">No ideas found</p>
                          <p className="text-sm">Try adjusting your search or filters to find what you're looking for.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50/50 transition-colors group">
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`px-6 py-4 ${cell.column.id === 'title' ? 'whitespace-normal' : 'whitespace-nowrap'} ${cell.column.id === 'actions' ? 'sticky right-0 bg-white group-hover:bg-slate-50/50 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.02)] z-10' : ''}`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {listData?.meta?.totalCount > 0 && (
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                <div className="text-sm text-slate-500">
                  Showing <span className="font-semibold text-slate-700">{(currentPage - 1) * 15 + 1}</span> to <span className="font-semibold text-slate-700">{Math.min(currentPage * 15, listData.meta.totalCount)}</span> of <span className="font-semibold text-slate-700">{listData.meta.totalCount}</span> ideas
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSearchParams(params => { params.set('page', (currentPage - 1).toString()); return params; })}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setSearchParams(params => { params.set('page', (currentPage + 1).toString()); return params; })}
                    disabled={currentPage * 15 >= listData.meta.totalCount}
                    className="px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div >
  );
}
